import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  BackButton,
  CancelButton,
  SubmitButton,
} from "../../../../Components/Utils/Buttons";
import { NuInput } from "../../../../Components/Utils/Input";
import FormikSelectTwo from "../../../../Components/Utils/Select";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import UseEditData from "../../../../Components/Hooks/useEditData";
import UseFetchCurrency from "../../../../Components/Hooks/useFetchCurrencyData";
import { CountryMIN } from "../../../../assets/CountryMini";
import axiosInstance from "../../../../Services/axiosCommon";

const EditBank = () => {
  const { bankEditId } = useParams();
  const location = useLocation();
  const stateData = location.state?.data;
  const fromUrl = location.state?.from || "/payments/banks";
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const { editData, isLoading, Oncancel } = UseEditData();
  const { value: currencyOptions } = UseFetchCurrency("/currency/drop-down");

  const [bankData, setBankData] = useState(stateData || null);
  const [dataLoading, setDataLoading] = useState(!stateData);

  useEffect(() => {
    const fetchBankDetails = async () => {
      try {
        setDataLoading(true);
        const res = await axiosInstance.get(`/banks/${bankEditId}`);
        if (res.data?.data) {
          setBankData(res.data.data);
        } else if (res.data) {
          setBankData(res.data);
        }
      } catch (err) {
        console.error("Error fetching bank:", err);
      } finally {
        setDataLoading(false);
      }
    };

    if (bankEditId) {
      fetchBankDetails();
    }
  }, [bankEditId]);

  const initialValues = {
    bankName: bankData?.bankName || "",
    accountName: bankData?.accountName || "",
    accountNumber: bankData?.accountNumber || "",
    iban: bankData?.iban || "",
    swiftCode: bankData?.swiftCode || "",
    currencyId: bankData?.currencyId || "",
    address1: bankData?.address?.address1 || bankData?.address1 || "",
    address2: bankData?.address?.address2 || bankData?.address2 || "",
    city: bankData?.address?.city || bankData?.city || "",
    district: bankData?.address?.district || bankData?.district || "",
    state: bankData?.address?.state || bankData?.state || "",
    country: bankData?.address?.country || bankData?.country || "Saudi Arabia",
    postalCode: bankData?.address?.postalCode || bankData?.postalCode || "",
  };

  const validationSchema = yup.object({
    bankName: yup.string().required("Bank Name is required"),
    accountName: yup.string().required("Account Name is required"),
    accountNumber: yup.string().required("Account Number is required"),
    iban: yup.string().required("IBAN is required"),
    swiftCode: yup.string().required("SWIFT Code is required"),
    address1: yup.string().required("Address Line 1 is required"),
    city: yup.string().required("City is required"),
    district: yup.string().required("District is required"),
    state: yup.string().required("State / Province is required"),
    country: yup.string().required("Country is required"),
    postalCode: yup.string().required("Postal Code is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        ...values,
        currencyId: values.currencyId ? Number(values.currencyId) : null,
      };
      editData(
        `/banks/edit-bank/${bankEditId}`,
        payload,
        resetForm,
        fromUrl,
        { currentPage: pageIndex, searchQuery }
      );
    },
  });

  if (dataLoading) {
    return (
      <div className="w-full h-full py-8 px-4 flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#048178] border-t-transparent rounded-full animate-spin mb-3"></div>
        <p className="text-sm font-medium text-gray-500">
          Loading Bank Information...
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full py-2 px-4">
      {/* Top Header */}
      <div className="flex justify-between items-center mb-2">
        <Breadcrumb
          items={[
            { name: "Payment", link: "/payments/banks" },
            { name: "Bank Details", link: "/payments/banks" },
            { name: "Edit" },
          ]}
        />
        <BackButton />
      </div>

      {/* Form Container */}
      <form onSubmit={formik.handleSubmit}>
        <div className="w-full mx-auto md:h-[calc(100vh-145px)] bg-white rounded-md mt-4 overflow-hidden flex flex-col justify-between">
          <div className="w-full h-full py-4 px-3 flex justify-start content-start flex-wrap overflow-y-auto">
            <NuInput
              label="Bank Name"
              placeholder="Bank Name"
              formik={formik}
              name="bankName"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="Account Name"
              placeholder="Account Name"
              formik={formik}
              name="accountName"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="Account Number"
              placeholder="Account Number"
              formik={formik}
              name="accountNumber"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="IBAN"
              placeholder="IBAN"
              formik={formik}
              name="iban"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="SWIFT / BIC Code"
              placeholder="SWIFT / BIC Code"
              formik={formik}
              name="swiftCode"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <FormikSelectTwo
              label="Currency"
              placeholder="Choose"
              Options={currencyOptions}
              formik={formik}
              name="currencyId"
              width="w-full md:w-1/2 lg:w-1/3"
              isClear
            />

            <NuInput
              label="Address Line 1"
              placeholder="Address Line 1"
              formik={formik}
              name="address1"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="Address Line 2"
              placeholder="Address Line 2"
              formik={formik}
              name="address2"
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="City"
              placeholder="City"
              formik={formik}
              name="city"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="District"
              placeholder="District"
              formik={formik}
              name="district"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="State / Province"
              placeholder="State / Province"
              formik={formik}
              name="state"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <FormikSelectTwo
              label="Country"
              placeholder="Choose"
              Options={CountryMIN}
              formik={formik}
              name="country"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <NuInput
              label="Postal Code"
              placeholder="Postal Code"
              formik={formik}
              name="postalCode"
              isRequired={true}
              width="w-full md:w-1/2 lg:w-1/3"
            />

            <div className="flex justify-end gap-2 w-full mt-4 pr-3">
              <CancelButton
                handleClick={() =>
                  Oncancel(fromUrl, {
                    currentPage: pageIndex,
                    searchQuery,
                  })
                }
              />
              <SubmitButton name="Save" isLoading={isLoading} />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditBank;
